import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Search, Menu, X, ChevronDown, Droplets, Candy, Flower2, Wind, Package, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo1 from '../assets/logo1.png';

const Navbar = ({ cartCount = 0 }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [shopDropdown, setShopDropdown] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  const navBg = scrolled || !isHome
    ? 'rgba(255,255,255,0.96)'
    : 'transparent';
  const navShadow = scrolled || !isHome ? '0 1px 0 rgba(0,0,0,0.06)' : 'none';
  const textColor = (!scrolled && isHome) ? 'var(--white)' : 'var(--charcoal)';

  const categories = [
    { name: 'THC Drinks', path: '/shop/drinks', Icon: Droplets },
    { name: 'Gummies', path: '/shop/gummies', Icon: Candy },
    { name: 'Flower', path: '/shop/flower', Icon: Flower2 },
    { name: 'Vapes', path: '/shop/vapes', Icon: Wind },
    { name: 'Bundles', path: '/shop/bundles', Icon: Package },
  ];

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: navBg,
        boxShadow: navShadow,
        backdropFilter: scrolled || !isHome ? 'blur(16px)' : 'none',
        transition: 'all 0.35s ease',
        height: '68px',
        display: 'flex', alignItems: 'center',
      }}>
        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          padding: '0 32px', width: '100%',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          {/* Logo */}
          <Link to="/" style={{
            display: 'flex', alignItems: 'center',
            textDecoration: 'none',
          }}>
            <img
              src={logo1}
              alt="Mind Blown Hemp Flower"
              className="nav-logo"
              style={{
                height: 'clamp(44px, 7vw, 68px)',
                width: 'auto',
                objectFit: 'contain',
                transition: 'opacity 0.35s',
                filter: (!scrolled && isHome) ? 'brightness(0) invert(1)' : 'none',
              }}
            />
          </Link>

          {/* Center nav links */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
          }} className="nav-links">
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => setShopDropdown(true)}
              onMouseLeave={() => setShopDropdown(false)}
            >
              <button style={{
                background: 'none', border: 'none',
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '8px 14px',
                fontFamily: 'DM Sans', fontWeight: 500, fontSize: '0.88rem',
                color: textColor, cursor: 'pointer',
                letterSpacing: '0.01em',
                transition: 'color 0.35s',
              }}>
                Shop <ChevronDown size={14} />
              </button>
              <AnimatePresence>
                {shopDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    style={{
                      position: 'absolute', top: '100%', left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'var(--white)',
                      borderRadius: '14px',
                      padding: '8px',
                      minWidth: '200px',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                      border: '1px solid var(--mist)',
                    }}
                  >
                    <Link
                      to="/shop"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '0.87rem', fontWeight: 600,
                        color: 'var(--forest)',
                        transition: 'background 0.15s',
                        textDecoration: 'none',
                        borderBottom: '1px solid var(--mist)',
                        marginBottom: '4px',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--smoke)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      All Products
                    </Link>
                    {categories.map(cat => (
                      <Link
                        key={cat.name}
                        to={cat.path}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          fontSize: '0.87rem', fontWeight: 500,
                          color: 'var(--charcoal)',
                          transition: 'background 0.15s',
                          textDecoration: 'none',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--smoke)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <cat.Icon size={15} color="var(--mid)" />
                        {cat.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {[
              { label: 'Lab Results', path: '/lab-results' },
              { label: 'Build a Box', path: '/build-box' },
              { label: 'About', path: '/about' },
            ].map(link => (
              <Link
                key={link.label}
                to={link.path}
                style={{
                  padding: '8px 14px',
                  fontFamily: 'DM Sans', fontWeight: 500, fontSize: '0.88rem',
                  color: textColor, letterSpacing: '0.01em',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  textDecoration: 'none',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              style={{
                background: 'none', border: 'none', padding: '8px',
                color: textColor, cursor: 'pointer',
                display: 'flex', alignItems: 'center',
                transition: 'color 0.35s',
                borderRadius: '50%',
              }}
            >
              <Search size={19} />
            </button>

            <Link to="/account" aria-label="Account" style={{
              padding: '8px', color: textColor, display: 'flex', alignItems: 'center',
              transition: 'color 0.35s', textDecoration: 'none',
            }}>
              <User size={19} />
            </Link>

            <Link to="/cart" style={{
              position: 'relative', padding: '8px',
              color: textColor, display: 'flex', alignItems: 'center',
              transition: 'color 0.35s',
              textDecoration: 'none',
            }}>
              <ShoppingBag size={19} />
              {cartCount > 0 && (
                <span style={{
                  position: 'absolute', top: '3px', right: '3px',
                  background: 'var(--gold)',
                  color: 'var(--white)',
                  borderRadius: '50%',
                  width: '16px', height: '16px',
                  fontSize: '0.62rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{cartCount}</span>
              )}
            </Link>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                display: 'none',
                background: 'none', border: 'none', padding: '8px',
                color: textColor, cursor: 'pointer',
              }}
              className="hamburger"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Search bar dropdown */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed', top: '68px', left: 0, right: 0,
              zIndex: 99,
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              padding: '20px 32px',
              borderBottom: '1px solid var(--mist)',
            }}
          >
            <div style={{
              maxWidth: '600px', margin: '0 auto',
              position: 'relative',
            }}>
              <Search
                size={18}
                style={{
                  position: 'absolute', left: '18px', top: '50%',
                  transform: 'translateY(-50%)', color: 'var(--mid)',
                }}
              />
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search gummies, drinks, flower..."
                style={{
                  width: '100%', padding: '14px 48px',
                  borderRadius: '999px',
                  border: '1.5px solid var(--mist)',
                  background: 'var(--smoke)',
                  fontFamily: 'DM Sans', fontSize: '0.95rem',
                  color: 'var(--charcoal)', outline: 'none',
                  transition: 'border 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--forest)'}
                onBlur={e => e.target.style.borderColor = 'var(--mist)'}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: '300px', zIndex: 200,
              background: 'var(--white)',
              boxShadow: '-20px 0 60px rgba(0,0,0,0.12)',
              padding: '80px 28px 28px',
              display: 'flex', flexDirection: 'column', gap: '4px',
            }}
          >
            {[
              { label: 'Shop All', path: '/shop', Icon: Package },
              ...categories.map(c => ({ label: c.name, path: c.path, Icon: c.Icon })),
              { label: 'Lab Results', path: '/lab-results', Icon: null },
              { label: 'Build a Box', path: '/build-box', Icon: null },
              { label: 'About', path: '/about', Icon: null },
              { label: 'Cart', path: '/cart', Icon: ShoppingBag },
            ].map(link => (
              <Link
                key={link.label}
                to={link.path}
                style={{
                  padding: '14px 16px',
                  fontFamily: 'DM Sans', fontWeight: 500, fontSize: '1rem',
                  color: 'var(--charcoal)', borderRadius: '12px',
                  transition: 'background 0.15s',
                  textDecoration: 'none',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--smoke)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {link.Icon && <link.Icon size={16} color="var(--mid)" />}
                {link.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 900px) {
          .nav-links { display: none !important; }
          .hamburger { display: flex !important; }
          .nav-logo { height: clamp(36px, 10vw, 52px) !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;